import React, { useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/kyrrypzn8V86bzUn4xxvS5/Screenshot_2026-07-03_at_12.13.14_PM.png',
  alt: 'Layered modern residence with dark timber screens, stone base, and tropical garden'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fMu9hFuX1MYqhuUTNL7Dza/Screenshot_2026-07-03_at_12.13.23_PM.png',
  alt: 'Timber-roofed twin-pavilion home rendering with pool and lush planting'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rPZUdPDfP7PPKSGQLRfoXm/unnamed_(1).jpg',
  alt: 'Illuminated two-story glass residence at twilight with landscaped garden'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dfbVr7D7y7YRLiJRTWz7RC/_11A7295-2-Edit.jpg',
  alt: 'Contemporary home with stone facade, deep eaves, and lush tropical landscaping along a driveway'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/cdT1HEFYK351kY5ZK13sXE/Screenshot_2026-07-03_at_12.12.07_PM.png',
  alt: 'Modern residence with white vertical fins, dark timber cladding, and tropical planting'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jLGGbbxpDwDZwNeFWSsyUS/Screenshot_2026-07-03_at_12.12.17_PM.png',
  alt: 'Multi-level home with glass balconies nestled among mature trees and greenery'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/t3sTLoSBSvco6C8UmmJHV6/Screenshot_2026-07-03_at_12.12.37_PM.png',
  alt: 'Curved white facade with vertical louvers and warm timber panels above a garden'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/956ZYNx1aEzAojDb9r4TAd/pasted-image.webp',
  alt: 'Modern glass architecture with wooden slat railing surrounded by greenery'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eXtx3wxqGZPn7QXLkZfmJk/pasted-image.webp',
  alt: 'Elegant dining room with wooden table, upholstered chairs, and floor-to-ceiling garden views'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pwFiYCWA5yYdTaNmKWSoHE/pasted-image.webp',
  alt: 'Multi-story contemporary home with glass balconies and tropical trees'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jtcpb4Uy29N8H7kiC91SQd/pasted-image.webp',
  alt: 'Glass pavilion at twilight with wooden fence and illuminated tree'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/w9gt9aomyyaErnAeC56qp8/pasted-image.webp',
  alt: 'Open-plan living space with floor-to-ceiling glass walls and garden view'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/uukxdryEXD6h47HShW3FZP/pasted-image.webp',
  alt: 'Tropical garden entrance with lush plantings and wooden gate'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/t2rPPQtzR2J1sXWUZfKxKX/pasted-image.webp',
  alt: 'Modern residence at dusk with warm interior lighting and bamboo landscaping'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mGeL3rPj9FQiuc7PvU6RQN/pasted-image.webp',
  alt: 'Contemporary facade with horizontal louvers and geometric volumes'
}];
export function ImageCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollByViewport = (dir: number) => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const threshold = 5;
    if (dir > 0) {
      // At (or near) the end -> loop back to the start
      if (el.scrollLeft >= maxScroll - threshold) {
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
        return;
      }
    } else {
      // At (or near) the start -> loop to the end
      if (el.scrollLeft <= threshold) {
        el.scrollTo({
          left: maxScroll,
          behavior: 'smooth'
        });
        return;
      }
    }
    el.scrollBy({
      left: dir * el.clientWidth,
      behavior: 'smooth'
    });
  };
  return <section className="pt-8 md:pt-12 pb-24 md:pb-32 bg-bg-warm" data-id="element-112">
      <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-113">
        {/* Header row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex items-center justify-between mb-8 gap-6" data-id="element-114">
          <div className="flex items-center space-x-4" data-id="element-115">
            <span className="h-px w-12 bg-accent block" data-id="element-116"></span>
            <span className="text-accent uppercase tracking-widest text-sm font-light" data-id="element-117">
              Our Work
            </span>
          </div>

          <div className="flex items-center gap-4 md:gap-8" data-id="element-118">
            <Link to="/projects/residential" className="hidden sm:flex text-text-muted uppercase tracking-widest text-sm font-light hover:text-accent transition-colors items-center space-x-2 border-b border-neutral-300 hover:border-accent pb-1" data-id="element-119">
              <span data-id="element-120">View All Projects</span>
              <ArrowUpRight size={16} data-id="element-121" />
            </Link>

            <div className="flex items-center gap-2" data-id="element-122">
              <button onClick={() => scrollByViewport(-1)} className="p-2.5 border border-neutral-300 rounded-full text-primary hover:text-accent hover:border-accent transition-colors duration-300" aria-label="Previous images" data-id="element-123">
                <ChevronLeft size={18} data-id="element-124" />
              </button>
              <button onClick={() => scrollByViewport(1)} className="p-2.5 border border-neutral-300 rounded-full text-primary hover:text-accent hover:border-accent transition-colors duration-300" aria-label="Next images" data-id="element-125">
                <ChevronRight size={18} data-id="element-126" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Track: 4 images visible at a time */}
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      ease: [0.16, 1, 0.3, 1]
    }} className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-127">
        <div ref={trackRef} className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory hide-scrollbar" data-id="element-128">
          {carouselImages.map((image, index) => <div key={index} className="snap-start flex-none" data-id="element-129">
              <div className="overflow-hidden bg-neutral-100" data-id="element-130">
                <img src={image.src} alt={image.alt} loading="lazy" className="h-[260px] md:h-[340px] w-auto max-w-none object-cover transition-transform duration-1000 hover:scale-105" data-id="element-131" />
              </div>
            </div>)}
        </div>
      </motion.div>
    </section>;
}